import React, { useState } from 'react';
import { createClient } from '@supabase/supabase-js';
import { Helmet } from 'react-helmet';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Constants
const META_TAGS = {
  title: 'The Easy QR - Create and Manage QR Codes Easily | Professional QR Code Generator',
  description: 'Create custom QR codes for your business with The Easy QR. Features include brand customization, analytics tracking, and easy sharing. Start generating QR codes today!',
  keywords: 'QR code generator, custom QR codes, QR code analytics, business QR codes',
  author: 'The Easy QR',
  ogTitle: 'The Easy QR - Professional QR Code Generator',
  ogDescription: 'Create custom QR codes for your business with The Easy QR. Features include brand customization, analytics tracking, and easy sharing.',
  ogType: 'website',
  ogUrl: 'https://theeasyqr.com',
  ogImage: 'https://theeasyqr.com/og-image.jpg',
  twitterCard: 'summary_large_image',
  twitterTitle: 'The Easy QR - Professional QR Code Generator',
  twitterDescription: 'Create custom QR codes for your business with The Easy QR. Features include brand customization, analytics tracking, and easy sharing.',
  canonicalUrl: 'https://theeasyqr.com',
};

const PRICING_TIERS = [
  {
    id: 'starter',
    title: 'Starter',
    price: '$4.99',
    features: ['10 QR codes', 'Customization'],
    excludedFeatures: ['Analytics'],
    description: 'Perfect for personal use',
  },
  {
    id: 'standard',
    title: 'Standard',
    price: '$19.99',
    features: ['50 QR codes', 'Customization'],
    excludedFeatures: ['Analytics'],
    description: 'Great for small businesses',
  },
  {
    id: 'premium',
    title: 'Premium',
    price: '$49.99',
    features: ['200 QR codes', 'Customization', 'Analytics'],
    description: 'Ideal for growing businesses',
    isPopular: true,
  },
  {
    id: 'business',
    title: 'Business',
    price: '$199.99',
    features: ['1000 QR codes', 'Customization', 'Analytics'],
    description: 'Best for large organizations',
  },
];

// Initialize Supabase client
const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

const supabase = createClient(supabaseUrl, supabaseKey);

// Debug log to check if environment variables are loaded
console.log('Supabase URL loaded:', !!supabaseUrl);
console.log('Supabase Key loaded:', !!supabaseKey);

// Reusable Components
const PricingTier = ({ tier }) => {
  const handleGetStartedClick = () => {
    const signupSection = document.getElementById('signups');
    if (signupSection) {
      signupSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className={`flex flex-col p-6 bg-white rounded-xl shadow-md hover:shadow-lg transition-shadow duration-300 ${tier.isPopular ? 'bg-gradient-to-br from-blue-500 to-blue-600 transform scale-105' : ''}`}>
      {tier.isPopular && (
        <div className="absolute top-0 right-0 bg-yellow-400 text-xs font-bold px-3 py-1 rounded-tr-xl rounded-bl-xl">
          POPULAR
        </div>
      )}
      <div className="text-center mb-6">
        <h3 className={`text-2xl font-bold ${tier.isPopular ? 'text-white' : 'text-gray-800'}`}>{tier.title}</h3>
        <div className="mt-4">
          <span className={`text-4xl font-bold ${tier.isPopular ? 'text-white' : 'text-gray-800'}`}>{tier.price}</span>
          <span className={`${tier.isPopular ? 'text-gray-200' : 'text-gray-600'}`}>/month</span>
        </div>
      </div>
      <ul className="space-y-3 mb-6 flex-grow">
        {tier.features.map((feature, index) => (
          <li key={index} className="flex items-center">
            <svg className="w-5 h-5 text-green-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
            </svg>
            <span className={tier.isPopular ? 'text-white' : 'text-gray-800'}>{feature}</span>
          </li>
        ))}
        {tier.excludedFeatures?.map((feature, index) => (
          <li key={index} className="flex items-center text-gray-500">
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
            <span>{feature}</span>
          </li>
        ))}
      </ul>
      <p className={`text-sm mb-4 ${tier.isPopular ? 'text-gray-200' : 'text-gray-600'}`}>{tier.description}</p>
      <button
        className={`w-full py-2 px-4 rounded-lg transition-colors duration-200 ${
          tier.isPopular ? 'bg-white hover:bg-gray-100 text-blue-600' : 'bg-blue-500 hover:bg-blue-600 text-white'
        }`}
        data-price-id={`price_${tier.id}`}
        onClick={handleGetStartedClick}
      >
        Get Started
      </button>
    </div>
  );
};

const FeatureCard = ({ title, description }) => (
  <div className="text-center">
    <h3 className="text-lg md:text-xl font-semibold mb-2">{title}</h3>
    <p className="text-sm md:text-base text-gray-600">{description}</p>
  </div>
);

// Main App Component
function App() {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);

  const validateInputs = () => {
    let isValid = true;
    if (!firstName.trim()) {
      toast.error('First Name is required.');
      isValid = false;
    }
    if (!lastName.trim()) {
      toast.error('Last Name is required.');
      isValid = false;
    }
    if (!email.trim()) {
      toast.error('Email is required.');
      isValid = false;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast.error('Please enter a valid email address.');
      isValid = false;
    }
    return isValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateInputs()) return;
    setLoading(true);
    
    if (!supabaseUrl || !supabaseKey) {
      toast.error('Supabase configuration is missing. Please check your environment variables.');
      setLoading(false);
      return;
    }

    try {
      const { error } = await supabase
        .from('signups')
        .insert([
          {
            first_name: firstName,
            last_name: lastName,
            email: email,
            created_at: new Date().toISOString()
          }
        ]);

      if (error) {
        console.error('Supabase error:', error);
        toast.error(error.message || 'Failed to submit form. Please try again.');
        return;
      }

      toast.success('Thank you for signing up! We will contact you soon.');
      setFirstName('');
      setLastName('');
      setEmail('');
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error(error.message || 'Failed to submit form. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-4 bg-gradient-to-br from-blue-400 to-blue-600">
      <Helmet>
        <title>{META_TAGS.title}</title>
        <meta name="description" content={META_TAGS.description} />
        <meta name="keywords" content={META_TAGS.keywords} />
        <meta name="author" content={META_TAGS.author} />
        <meta property="og:title" content={META_TAGS.ogTitle} />
        <meta property="og:description" content={META_TAGS.ogDescription} />
        <meta property="og:type" content={META_TAGS.ogType} />
        <meta property="og:url" content={META_TAGS.ogUrl} />
        <meta property="og:image" content={META_TAGS.ogImage} />
        <meta name="twitter:card" content={META_TAGS.twitterCard} />
        <meta name="twitter:title" content={META_TAGS.twitterTitle} />
        <meta name="twitter:description" content={META_TAGS.twitterDescription} />
        <link rel="canonical" href={META_TAGS.canonicalUrl} />
      </Helmet>

      <ToastContainer position="top-center" autoClose={5000} hideProgressBar={false} newestOnTop={false} closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover theme="colored" />

      <header className="text-center mb-8 px-4 text-white">
        <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 pt-36">Start Your QR Code Journey</h1>
        <p className="text-xl md:text-2xl text-gray-200 mt-4 max-w-2xl mx-auto">
          Create professional QR codes in minutes. Sign up and get 10 free QR codes on us.
        </p>
      </header>

      <main id="signup" className="bg-white p-6 md:p-8 rounded-lg shadow-lg w-full max-w-md">
        <h2 className="text-2xl font-bold text-center mb-6 text-gray-800">Get Started Today</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <input type="text" placeholder="First Name" value={firstName} onChange={(e) => setFirstName(e.target.value)} className="w-full px-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-300" required />
          <input type="text" placeholder="Last Name" value={lastName} onChange={(e) => setLastName(e.target.value)} className="w-full px-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-300" required />
          <input type="email" placeholder="Enter your email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full px-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-300" required />
          <button type="submit" className="w-full bg-gradient-to-r from-blue-500 to-purple-500 text-white py-2 rounded-lg hover:bg-gradient-to-l" disabled={loading}>
            {loading ? 'Submitting...' : 'Sign Up'}
          </button>
        </form>
      </main>

      <nav className="flex justify-center space-x-6 mt-8 text-white">
        <a href="#features" className="hover:text-blue-400 transition-colors duration-300 text-lg">Features</a>
        <a href="#how-it-works" className="hover:text-blue-400 transition-colors duration-300 text-lg">How It Works</a>
        <a href="#pricing" className="hover:text-blue-400 transition-colors duration-300 text-lg">Pricing</a>
      </nav>

      <section id="features" className="w-full max-w-4xl mt-12 p-8 bg-white rounded-lg shadow-lg">
        <h2 className="text-3xl md:text-4xl font-bold text-center mb-8 text-gray-800">Powerful Features</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
          <FeatureCard title="Easy QR Code Generation" description="Quickly generate QR codes for URLs, text, and more with just a few clicks." />
          <FeatureCard title="Customizable Designs" description="Customize your QR codes with colors, logos, and frames to match your brand." />
          <FeatureCard title="Analytics Tracking" description="Track scans and gather insights with our built-in analytics dashboard." />
        </div>
      </section>

      <section id="how-it-works" className="w-full max-w-4xl mt-12 p-8 bg-white rounded-lg shadow-lg">
        <h2 className="text-3xl md:text-4xl font-bold text-center mb-8 text-gray-800">How It Works</h2>
        <div className="space-y-4 md:space-y-6">
          {[1, 2, 3].map((step) => (
            <div key={step} className="flex items-start">
              <div className="bg-blue-500 text-white rounded-full p-3 mr-4">{step}</div>
              <div>
                <h3 className="text-lg md:text-xl font-semibold mb-2">Step {step}</h3>
                <p className="text-sm md:text-base text-gray-600">{step === 1 ? 'Join us today and get access to your free trial. We’ll send you an email as soon as your account is ready to go! We launch in early 2025. Be the first to use our service.' : step === 2 ? 'Open your inbox, click the verification link, and confirm your email address. You’re just moments away from creating stunning QR Codes.' : step === 3 ? 'Start designing your QR Codes right away! Enjoy 10 free downloads and unlimited social shares—no strings attached, no monthly commitments.' : ''}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section id="pricing" className="w-full max-w-6xl mt-32 p-8 bg-white rounded-lg shadow-lg">
        <h2 className="text-3xl md:text-4xl font-bold text-center mb-8 text-gray-800">Simple, Transparent Pricing</h2>
        <p className="text-center text-gray-600 mb-12 max-w-2xl mx-auto">Choose the perfect plan for your needs. All plans include our core features with no hidden fees.</p>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {PRICING_TIERS.map((tier) => (
            <PricingTier key={tier.id} tier={tier} />
          ))}
        </div>
       
      </section>

      <footer className="mt-12 py-6 text-center text-white bg-gradient-to-r from-blue-600 to-blue-800">
        <div className="max-w-4xl mx-auto px-4">
          <p className="text-sm md:text-base mb-2">&copy; {new Date().getFullYear()} theeasyqr.com All rights reserved.</p>
          
        </div>
      </footer>
    </div>
  );
}

export default App;